import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { focusFirstError } from "@/lib/formA11y";
import { formatWhen, lifecycleLabel } from "@/lib/format";
import { newIdempotencyKey } from "@/lib/idempotency";
import {
  ApiClientError,
  type Project,
  type WorkspaceProfile,
  type WorkspaceRequest,
} from "@/lib/types";
import {
  WORKSPACE_MEMORY_OPTIONS,
  WORKSPACE_NOTES_MAX,
  WORKSPACE_PAGE_LIMIT,
  validateWorkspaceRequestForm,
  workspaceRequestFingerprint,
} from "@/lib/workspaceRequest";
import { AuthStatePanels } from "@/components/AuthState";
import { Button } from "@/components/Button";
import { ConfirmSheet } from "@/components/ConfirmSheet";
import { Field, TextArea, TextInput } from "@/components/Field";
import { StatePanel } from "@/components/StatePanel";
import { StatusRail } from "@/components/StatusRail";
import { WorkspaceRequestPlanPanel } from "@/components/WorkspaceRequestPlan";
import { useLocale } from "@/locale/LocaleContext";
import { useOnline } from "@/lib/offline";
import shell from "@/components/shell.module.css";
import ui from "@/components/ui.module.css";

function requestRail(state: string): string {
  if (state === "declined" || state === "canceled") return "failed";
  if (state === "reviewing") return "running";
  return "queued";
}

/** Keyed by projectId so navigating projects remounts a clean form/list identity. */
export function WorkspaceRequestsPage() {
  const { projectId = "" } = useParams();
  return <WorkspaceRequestsView key={projectId} projectId={projectId} />;
}

function WorkspaceRequestsView({ projectId }: { projectId: string }) {
  const { m, locale, fmt } = useLocale();
  const online = useOnline();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageRaw = Number(searchParams.get("page") || "1");
  const page = Number.isInteger(pageRaw) && pageRaw > 0 ? Math.min(pageRaw, 10000) : 1;

  const [project, setProject] = useState<Project | null>(null);
  const [profiles, setProfiles] = useState<WorkspaceProfile[]>([]);
  const [items, setItems] = useState<WorkspaceRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState<ApiClientError | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [listRetry, setListRetry] = useState(0);

  const [profile, setProfile] = useState("interactive");
  const [memory, setMemory] = useState("32");
  const [storage, setStorage] = useState("256");
  const [duration, setDuration] = useState("7");
  const [notes, setNotes] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<WorkspaceRequest | null>(null);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const idemRef = useRef(newIdempotencyKey());
  const fingerprintRef = useRef("");
  const projectIdRef = useRef(projectId);
  const generationRef = useRef(0);
  const catalogInitRef = useRef(false);
  const errorMessageRef = useRef(m.states.error);
  const loadFailedRef = useRef(m.workspace.loadFailed);
  errorMessageRef.current = m.states.error;
  loadFailedRef.current = m.workspace.loadFailed;
  projectIdRef.current = projectId;

  useEffect(() => {
    generationRef.current += 1;
    catalogInitRef.current = false;
    fingerprintRef.current = "";
    idemRef.current = newIdempotencyKey();
    setProfile("interactive");
    setMemory("32");
    setStorage("256");
    setDuration("7");
    setNotes("");
    setFieldErrors({});
    setFormError(null);
    setBusy(false);
    setCancelTarget(null);
    setCancelBusy(false);
    setCancelError(null);
    setItems([]);
    setTotal(0);
    setListError(null);
    setProject(null);
    setProfiles([]);
    setError(null);
  }, [projectId]);

  useEffect(() => {
    let alive = true;
    const ac = new AbortController();
    const generation = generationRef.current;
    setLoading(true);
    Promise.all([api.project(projectId), api.workspaceProfiles(ac.signal)])
      .then(([p, catalog]) => {
        if (!alive || generation !== generationRef.current || projectIdRef.current !== projectId) {
          return;
        }
        setProject(p);
        setProfiles(catalog.items);
        // Seed default profile once per project mount — never on locale-driven re-renders.
        if (!catalogInitRef.current) {
          catalogInitRef.current = true;
          const first = catalog.items[0]?.id;
          if (first) setProfile(first);
        }
        setError(null);
      })
      .catch((e) => {
        if (!alive || ac.signal.aborted) return;
        if (generation !== generationRef.current || projectIdRef.current !== projectId) return;
        setError(
          e instanceof ApiClientError
            ? e
            : new ApiClientError(0, "error", errorMessageRef.current),
        );
      })
      .finally(() => {
        if (alive && generation === generationRef.current && projectIdRef.current === projectId) {
          setLoading(false);
        }
      });
    return () => {
      alive = false;
      ac.abort();
    };
  }, [projectId]);

  useEffect(() => {
    let alive = true;
    const ac = new AbortController();
    const generation = generationRef.current;
    setListLoading(true);
    setListError(null);
    api
      .projectWorkspaceRequests(projectId, { page, limit: WORKSPACE_PAGE_LIMIT }, ac.signal)
      .then((res) => {
        if (!alive || generation !== generationRef.current || projectIdRef.current !== projectId) {
          return;
        }
        setItems(res.items);
        setTotal(res.total);
        setListError(null);
        const maxPage = Math.max(1, Math.ceil(res.total / WORKSPACE_PAGE_LIMIT));
        if (res.page > maxPage) {
          const next = new URLSearchParams();
          if (maxPage > 1) next.set("page", String(maxPage));
          setSearchParams(next, { replace: true });
        }
      })
      .catch((e) => {
        if (!alive || ac.signal.aborted) return;
        if (generation !== generationRef.current || projectIdRef.current !== projectId) return;
        setItems([]);
        setTotal(0);
        setListError(e instanceof Error ? e.message : loadFailedRef.current);
      })
      .finally(() => {
        if (alive && generation === generationRef.current && projectIdRef.current === projectId) {
          setListLoading(false);
        }
      });
    return () => {
      alive = false;
      ac.abort();
    };
  }, [projectId, page, listRetry, setSearchParams]);

  function stillOnProject(capturedProjectId: string, generation: number): boolean {
    return (
      projectIdRef.current === capturedProjectId && generationRef.current === generation
    );
  }

  function ensureIdempotency(fingerprint: string) {
    if (fingerprintRef.current !== fingerprint) {
      fingerprintRef.current = fingerprint;
      idemRef.current = newIdempotencyKey();
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!online) {
      setFormError(m.common.onlineRequired);
      return;
    }
    const validated = validateWorkspaceRequestForm(
      { profile, memory, storage, duration, notes },
      {
        profileRequired: m.workspace.profileRequired,
        memoryRequired: m.workspace.memoryRequired,
        storageRange: m.workspace.storageRange,
        durationRange: m.workspace.durationRange,
        notesMax: m.workspace.notesMax,
      },
    );
    if (!validated.ok) {
      setFieldErrors(validated.errors as Record<string, string>);
      queueMicrotask(() =>
        focusFirstError(validated.errors as Record<string, string>, [
          "wr-profile",
          "wr-memory",
          "wr-storage",
          "wr-duration",
          "wr-notes",
        ]),
      );
      return;
    }
    setFieldErrors({});
    const capturedProjectId = projectId;
    const generation = generationRef.current;
    const fingerprint = workspaceRequestFingerprint(capturedProjectId, validated.payload);
    ensureIdempotency(fingerprint);
    setBusy(true);
    setFormError(null);
    try {
      await api.createWorkspaceRequest(capturedProjectId, validated.payload, idemRef.current);
      if (!stillOnProject(capturedProjectId, generation)) return;
      setNotes("");
      fingerprintRef.current = "";
      idemRef.current = newIdempotencyKey();
      if (page > 1) {
        setSearchParams(new URLSearchParams(), { replace: true });
      } else {
        setListRetry((n) => n + 1);
      }
    } catch (err) {
      if (!stillOnProject(capturedProjectId, generation)) return;
      if (err instanceof ApiClientError && err.status === 409) {
        setFormError(m.workspace.conflictActive);
        fingerprintRef.current = "";
        idemRef.current = newIdempotencyKey();
        setListRetry((n) => n + 1);
      } else {
        setFormError(err instanceof Error ? err.message : m.workspace.submitFailed);
      }
    } finally {
      if (stillOnProject(capturedProjectId, generation)) setBusy(false);
    }
  }

  async function onCancel() {
    if (!cancelTarget) return;
    const capturedProjectId = projectId;
    const generation = generationRef.current;
    const target = cancelTarget;
    setCancelBusy(true);
    setCancelError(null);
    try {
      await api.cancelWorkspaceRequest(target.id, { version: target.version });
      if (!stillOnProject(capturedProjectId, generation)) return;
      setCancelTarget(null);
      setListRetry((n) => n + 1);
    } catch (err) {
      if (!stillOnProject(capturedProjectId, generation)) return;
      if (err instanceof ApiClientError && err.status === 409) {
        setCancelError(m.workspace.cancelConflict);
        setListRetry((n) => n + 1);
      } else {
        setCancelError(err instanceof Error ? err.message : m.workspace.cancelFailed);
      }
    } finally {
      if (stillOnProject(capturedProjectId, generation)) setCancelBusy(false);
    }
  }

  const selectedProfile = profiles.find((p) => p.id === profile);
  const pageCount = Math.max(1, Math.ceil(total / WORKSPACE_PAGE_LIMIT));
  const canCancel = (item: WorkspaceRequest) =>
    item.state === "requested" || item.state === "reviewing";

  return (
    <AuthStatePanels
      loading={loading}
      error={error}
      empty={!project ? <StatePanel kind="empty" title={m.states.empty} /> : null}
    >
      {project ? (
        <div>
          <p className={ui.meta}>
            <Link to={`/app/projects/${projectId}`}>← {project.name}</Link>
          </p>
          <h1 className={shell.pageTitle}>{m.workspace.title}</h1>
          <p className={shell.pageLead}>{m.workspace.lead}</p>
          <p className={ui.meta}>{m.workspace.bounds}</p>

          <form
            noValidate
            onSubmit={onSubmit}
            className={ui.panel}
            style={{ display: "grid", gap: "0.75rem", marginTop: "1.25rem" }}
          >
            <h2 className="display" style={{ margin: 0, fontSize: "1.1rem" }}>
              {m.workspace.newTitle}
            </h2>

            <Field label={m.workspace.profile} htmlFor="wr-profile" error={fieldErrors["wr-profile"]}>
              <select
                id="wr-profile"
                className={ui.input}
                value={profile}
                onChange={(e) => setProfile(e.target.value)}
              >
                {profiles.length === 0 ? (
                  <option value="interactive">{lifecycleLabel(m, "interactive")}</option>
                ) : (
                  profiles.map((p) => (
                    <option key={p.id} value={p.id}>
                      {lifecycleLabel(m, p.id)}
                    </option>
                  ))
                )}
              </select>
            </Field>
            {selectedProfile ? (
              <p className={ui.meta}>
                {m.workspace.toolsLabel}:{" "}
                {selectedProfile.requested_tools.length
                  ? selectedProfile.requested_tools
                      .map((tool) => lifecycleLabel(m, tool))
                      .join(", ")
                  : "—"}
                {" · "}
                {lifecycleLabel(m, selectedProfile.availability)}
              </p>
            ) : null}

            <Field
              label={m.workspace.memory}
              htmlFor="wr-memory"
              error={fieldErrors["wr-memory"]}
              hint={m.workspace.memoryHint}
            >
              <select
                id="wr-memory"
                className={ui.input}
                value={memory}
                onChange={(e) => setMemory(e.target.value)}
              >
                {WORKSPACE_MEMORY_OPTIONS.map((gb) => (
                  <option key={gb} value={String(gb)}>
                    {fmt(m.workspace.memoryOption, { gb })}
                  </option>
                ))}
              </select>
            </Field>

            <Field
              label={m.workspace.storage}
              htmlFor="wr-storage"
              error={fieldErrors["wr-storage"]}
              hint={m.workspace.storageHint}
            >
              <TextInput
                inputMode="numeric"
                value={storage}
                onChange={(e) => setStorage(e.target.value)}
              />
            </Field>

            <Field
              label={m.workspace.duration}
              htmlFor="wr-duration"
              error={fieldErrors["wr-duration"]}
              hint={m.workspace.durationHint}
            >
              <TextInput
                inputMode="numeric"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
              />
            </Field>

            <Field
              label={m.workspace.notes}
              htmlFor="wr-notes"
              error={fieldErrors["wr-notes"]}
              hint={m.workspace.notesHint}
            >
              <TextArea
                className="resize-none"
                style={{ resize: "none" }}
                value={notes}
                maxLength={WORKSPACE_NOTES_MAX}
                onChange={(e) => setNotes(e.target.value.slice(0, WORKSPACE_NOTES_MAX))}
              />
            </Field>

            {formError ? (
              <p className={ui.fieldError} role="alert">
                {formError}
              </p>
            ) : null}
            <Button type="submit" disabled={busy || !online} aria-busy={busy || undefined}>
              {busy ? m.common.sending : m.workspace.submit}
            </Button>
          </form>

          <section style={{ marginTop: "1.5rem" }}>
            <h2 className="display" style={{ fontSize: "1.1rem" }}>
              {m.workspace.listTitle}
            </h2>
            {listLoading ? <StatePanel kind="pending" title={m.common.loading} /> : null}
            {listError ? (
              <div role="alert">
                <p className={ui.fieldError}>{listError}</p>
                <Button type="button" variant="secondary" onClick={() => setListRetry((n) => n + 1)}>
                  {m.workspace.retry}
                </Button>
              </div>
            ) : null}
            {!listLoading && !listError && items.length === 0 ? (
              <StatePanel kind="empty" title={m.workspace.empty} message={m.workspace.emptyBody} />
            ) : null}
            {!listLoading && !listError
              ? items.map((item) => (
                  <div key={item.id} className={ui.row} style={{ cursor: "default" }}>
                    <StatusRail state={requestRail(item.state)} />
                    <div style={{ width: "100%" }}>
                      <strong>{lifecycleLabel(m, item.profile)}</strong>
                      <div className={ui.meta}>
                        {lifecycleLabel(m, item.state)} ·{" "}
                        {fmt(m.workspace.summary, {
                          memory: item.memory_gb,
                          storage: item.storage_gb,
                          days: item.duration_days,
                        })}
                      </div>
                      <div className={ui.meta}>{formatWhen(item.created_at, locale)}</div>
                      {item.notes ? (
                        <p className={ui.meta} style={{ marginTop: "0.35rem" }}>
                          <strong>{m.workspace.notes}:</strong> {item.notes}
                        </p>
                      ) : null}
                      {item.review_note ? (
                        <p className={ui.meta} style={{ marginTop: "0.35rem" }}>
                          <strong>{m.workspace.reviewNoteLabel}:</strong> {item.review_note}
                        </p>
                      ) : null}
                      <WorkspaceRequestPlanPanel requestId={item.id} source="customer" />
                      {canCancel(item) ? (
                        <div
                          className={ui.actions}
                          style={{ justifyContent: "flex-start", marginTop: "0.55rem" }}
                        >
                          <Button
                            type="button"
                            variant="danger"
                            onClick={() => {
                              setCancelError(null);
                              setCancelTarget(item);
                            }}
                          >
                            {m.workspace.cancel}
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))
              : null}

            {!listLoading && !listError && total > WORKSPACE_PAGE_LIMIT ? (
              <div className={ui.actions} style={{ justifyContent: "flex-start", marginTop: "0.75rem" }}>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={page <= 1}
                  onClick={() => {
                    const next = new URLSearchParams();
                    if (page - 1 > 1) next.set("page", String(page - 1));
                    setSearchParams(next, { replace: true });
                  }}
                >
                  {m.workspace.prevPage}
                </Button>
                <span className={ui.meta}>
                  {fmt(m.workspace.pageStatus, { page, pages: pageCount, total })}
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={page >= pageCount}
                  onClick={() => {
                    const next = new URLSearchParams();
                    next.set("page", String(page + 1));
                    setSearchParams(next, { replace: true });
                  }}
                >
                  {m.workspace.nextPage}
                </Button>
              </div>
            ) : null}
          </section>

          <ConfirmSheet
            open={Boolean(cancelTarget)}
            title={m.workspace.cancelTitle}
            confirmLabel={m.workspace.cancelConfirm}
            danger
            busy={cancelBusy}
            onClose={() => {
              if (cancelBusy) return;
              setCancelTarget(null);
              setCancelError(null);
            }}
            onConfirm={() => void onCancel()}
          >
            <p>{m.workspace.cancelBody}</p>
            {cancelError ? (
              <p className={ui.fieldError} role="alert">
                {cancelError}
              </p>
            ) : null}
          </ConfirmSheet>
        </div>
      ) : null}
    </AuthStatePanels>
  );
}
